<script setup>
import { ref } from "vue";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import RecodeTypePicker from "@/components/Survey/ResultsV2/RecodeTypePicker.vue";
import { recodeFormComponent } from "@/components/Survey/ResultsV2/recodeFormComponent";
import { useResultsV2RecodesStore } from "@stores/survey/resultsV2/resultsV2.recodes";
import { useGridRowsStore } from "@stores/survey/grids/gridRows";
import { useGridsStore } from "@stores/survey/grids/grids";
import genToken from "@utils/generateToken";

const recodesStore = useResultsV2RecodesStore();
const gridRowsStore = useGridRowsStore();

const pickerOpen = ref(false);
const formOpen = ref(false);
const recodeToken = ref(null);
const recodeKind = ref(null);
const isNew = ref(false);

function openNew() {
  pickerOpen.value = true;
}

function openEdit(cardToken) {
  recodesStore.initExistingRecode(cardToken, { surface: "interviews" });
  const config = recodesStore.recodeConfigs[cardToken];
  if (!config) return;
  recodeToken.value = cardToken;
  recodeKind.value = config.recodeKind;
  isNew.value = false;
  formOpen.value = true;
}

function onSelectType(kind) {
  const token = genToken();
  recodesStore.initNewRecode(token, kind, { surface: "interviews" });
  recodeToken.value = token;
  recodeKind.value = kind;
  isNew.value = true;
  formOpen.value = true;
}

function close() {
  formOpen.value = false;
  if (recodeToken.value) recodesStore.cleanup(recodeToken.value);
  recodeToken.value = null;
  recodeKind.value = null;
}

function onSave() {
  close();
}

function onCancel() {
  close();
}

async function onDelete() {
  const token = recodeToken.value;
  formOpen.value = false;
  if (token) await recodesStore.deleteRecode(token);
  recodeToken.value = null;
  recodeKind.value = null;
  await Promise.all([gridRowsStore.fetch(), gridRowsStore.fetchColumnOptions(), useGridsStore().fetch()]);
}

defineExpose({ openNew, openEdit });
</script>

<template>
  <RecodeTypePicker v-model:open="pickerOpen" @select="onSelectType" />

  <Dialog v-model:open="formOpen">
    <DialogContent class="p-0 max-w-2xl max-h-[85vh] overflow-y-auto">
      <DialogHeader class="sr-only">
        <DialogTitle>Recode</DialogTitle>
        <DialogDescription>Configure the recode</DialogDescription>
      </DialogHeader>
      <component
        :is="recodeFormComponent(recodeKind)"
        v-if="recodeToken && recodeKind"
        :card-token="recodeToken"
        :is-new="isNew"
        @save="onSave"
        @cancel="onCancel"
        @delete="onDelete" />
    </DialogContent>
  </Dialog>
</template>
